import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { Subscription } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

interface PricingLeverSliderConfig {
  widgetId?: string;
  title?: string;
  highlightText?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  inputLabel?: string;
  suffix?: string;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  key?: string;
  isQueryParam?: boolean;
  apiUrl?: string;
  method?: string;
  data?: unknown;
  dataPath?: string;
  valueKey?: string;
  minKey?: string;
  maxKey?: string;
  stepKey?: string;
  height?: string;
  heightSm?: string;
  heightMd?: string;
  heightLg?: string;
}

@Component({
  selector: 'app-pricing-lever-slider',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './pricing-lever-slider.component.html',
  styleUrls: ['./pricing-lever-slider.component.scss'],
})
export class PricingLeverSliderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data?: PricingLeverSliderConfig;
  @Output() valueChange = new EventEmitter<number>();

  min: number | null = null;
  max: number | null = null;
  step: number | null = null;
  value: number | null = null;
  inputValue: number | null = null;
  private committedValue: number | null = null;
  fontScale = 1;
  errorMessage = '';
  configReady = false;

  private readonly configService = inject(ConfigService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private dataSub?: Subscription;
  private reflowSub?: Subscription;
  private lastEmittedValue: number | null = null;

  ngOnInit(): void {
    this.applyResponsiveSizing();
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.applyResponsiveSizing());
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.applyResponsiveSizing();
      this.loadConfig();
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
  }

  @HostBinding('style.--pricing-slider-font-scale')
  get hostFontScale(): string {
    return this.fontScale.toString();
  }

  get suffix(): string {
    return this.data?.suffix ?? '%';
  }

  get formattedMin(): string {
    return this.formatPercent(this.min ?? 0);
  }

  get formattedMax(): string {
    return this.formatPercent(this.max ?? 0);
  }

  get formattedValue(): string {
    return this.value === null ? '' : this.formatPercent(this.value);
  }

  onSliderInput(nextValue: number): void {
    this.commitValue(nextValue);
  }

  onInputChange(): void {
    this.commitValue(this.inputValue);
  }

  private loadConfig(): void {
    this.dataSub?.unsubscribe();
    this.errorMessage = '';

    if (!this.data?.apiUrl) {
      this.applyConfig(this.data);
      return;
    }

    this.dataSub = this.configService.getConfig(this.data.apiUrl, this.data, {}, this.data.method).subscribe({
      next: (response) => this.applyConfig(response),
      error: () => {
        this.errorMessage = 'Unable to load pricing lever data.';
        this.applyConfig(this.data);
      },
    });
  }

  private applyConfig(source: unknown): void {
    const record = this.resolveConfigRecord(source);

    const min = this.readRequiredNumber(record, this.data?.minKey || 'min', this.data?.min);
    const max = this.readRequiredNumber(record, this.data?.maxKey || 'max', this.data?.max);
    const step = this.readRequiredNumber(record, this.data?.stepKey || 'step', this.data?.step);

    const valueKey = this.data?.valueKey || 'value';
    const value = this.readRequiredNumber(record, valueKey, this.data?.value ?? this.data?.defaultValue);

    if (min === null || max === null || step === null || value === null) {
      this.configReady = false;
      this.errorMessage = 'Pricing lever config must provide min, max, step, and value.';
      return;
    }

    this.min = min;
    this.max = max;
    this.step = step;
    this.configReady = true;
    this.setDisplayValue(value);
    this.committedValue = this.value;
    this.lastEmittedValue = this.value;
  }

  private resolveConfigRecord(source: unknown): Record<string, unknown> | null {
    const configuredPath = this.data?.dataPath;
    const candidates = [
      configuredPath ? this.resolvePath(source, configuredPath) : undefined,
      this.resolvePath(source, 'responseList.0.data.0.pricingLever'),
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'data.0.pricingLever'),
      this.resolvePath(source, 'data.0'),
      this.data?.data,
      source,
    ];

    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as Record<string, unknown>;
      }
    }

    return null;
  }

  private commitValue(rawValue: unknown, emit = true): void {
    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    this.setDisplayValue(rawValue);

    if (!emit) {
      return;
    }

    if (this.value === null || this.committedValue === this.value || this.lastEmittedValue === this.value) {
      return;
    }

    this.committedValue = this.value;
    this.lastEmittedValue = this.value;
    this.valueChange.emit(this.value);
    const filterKey = this.data?.filterKey || this.data?.key;

    if (filterKey) {
      const filterPayload = this.buildFilterPayload(filterKey, this.value);
      this.chartFilterHandler.updateFilterSelection(
        { ...this.data, filterKey },
        filterPayload,
        filterPayload,
      );
    }
  }

  private setDisplayValue(rawValue: unknown): void {
    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    const nextValue = this.clamp(this.toNumber(rawValue, this.value ?? this.min));
    const decimals = this.decimalPlaces(this.step);
    this.value = Number(nextValue.toFixed(decimals));
    this.inputValue = this.value;
  }

  private buildFilterPayload(filterKey: string | string[], value: number): Record<string, number> {
    const keys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);

    return keys.reduce<Record<string, number>>((payload, key) => {
      payload[key] = value;
      return payload;
    }, { value });
  }

  private applyResponsiveSizing(): void {
    this.fontScale = this.responsiveLayoutService.getFontScale();
  }

  private formatPercent(value: number): string {
    return `${value.toFixed(2)}${this.suffix}`;
  }

  private toNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private readRequiredNumber(
    record: Record<string, unknown> | null,
    key: string,
    fallback?: number,
  ): number | null {
    const raw = record?.[key] ?? fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clamp(value: number): number {
    if (this.min === null || this.max === null) {
      return value;
    }

    return Math.min(this.max, Math.max(this.min, value));
  }

  private decimalPlaces(value: number): number {
    const text = String(value);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }

  private resolvePath(source: unknown, path: string): unknown {
    if (!path) {
      return undefined;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }
      if (typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }
      return undefined;
    }, source);
  }
}
